import React, { useState } from "react";
import List from "../workingPartList";
import FrontendTable from "../workingPartFrontendTable";
import Filter from "../workingPartFilter";
import { useTranslation } from "react-i18next";
import PATHS from "../../../../constants/paths";
import Breadcrumb from "../../../utils/breadCrumbs";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ButtonShowError } from "./workingPartFilterList.styled";
import * as WorkingPartServices from "../../../../services/WorkingPartServices";
import * as GeneralServices from "../../../../services/GeneralServices";
import * as XLSX from "xlsx";
import moment from "moment-timezone";
import useFilterState from "../../../../utils/hooks/useFilterState";

const queryClient = new QueryClient();

const WorkingPartFilterListContainer = (props) => {
  const { t } = useTranslation();
  const breads = [
    { label: t("home"), path: PATHS.HOME },
    { label: t("forms.workingPartSnap.title") },
  ];
  const [filters, setFilters] = useFilterState("workingPartSnap");
  const [showError, setShowError] = useState(false);
  const [allData, setAllData] = useState([]);
  const [loadingAllData, setLoadingAllData] = useState(false);
  const navigate = useNavigate();

  // Función para cargar todos los datos sin paginación
  const loadAllData = async () => {
    setLoadingAllData(true);
    try {
      const filter = {
        page: 1,
        pageSize: 9999, // Número grande para obtener todos
      };
      if (filters) {
        filter.filters = filters;
      }
      const result = await WorkingPartServices.getAllSnapshots(filter);
      setAllData(result || []);
    } catch (error) {
      console.error("Error al cargar todos los datos:", error);
      setAllData([]);
    } finally {
      setLoadingAllData(false);
    }
  };

  // Cargar datos cuando cambian los filtros
  React.useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handlerCleanFilter = async (resetForm) => {
    resetForm();
    setFilters({ showError });
  };

  const handlerOnSubmitFilter = (values) => {
    let newFilter = {};
    const valuesFilters = Object.entries(values);
    valuesFilters.forEach((value) => {
      if (value[1] !== undefined && value[1].toString().length > 0) {
        newFilter[value[0]] = value[1];
      }
    });

    setFilters(newFilter);
  };

  const goToRegister = () => {
    // navigate(PATHS.GENERATE_ACCOUNT_LINKS);
  };

  const handlerFilterEmployee = async (value) => {
    let filter = {
      filters: {
        companyCode: value,
      },
    };
    let result = await GeneralServices.listFilterVisualEmployees(filter);
    return result;
  };

  const exportToExcel = async () => {
    const filter = {
      filters,
    };
    const parcels = await GeneralServices.getParcels();
    WorkingPartServices.listToExport(filter).then((result) => {
      const data = (result || []).flatMap((item) => {
        const periodo = `${item.month}/${item.year}`;
        const employeeCode = item.employeeCode?.[0] || "";
        const employeeName = item.employeeName || "";
        const contractType = item.employeeData?.indefinedContract
          ? "Sal. Mens"
          : "Sal. JJRR";

        const status = `${
          item.conceptData?.status === "PENDING" ? "Si" : "No"
        }/${item.conceptData?.sendedA3 ? "Si" : "No"}`;

        const peonadasTotal = `${Math.trunc(item.peonadas) || 0}/${
          item.totalParts || 0
        }`;

        // Aggregate data by farm (a farm can contain multiple parcels)
        const rounded = (num) => Math.round(num * 100) / 100;
        const farmMap = {};

        Object.entries(item.hourTypeByParcel || {}).forEach(
          ([parcelId, hourTypes]) => {
            const normalHours = rounded(hourTypes["1"] || 0);
            const extraHours = rounded(hourTypes["2"] || 0);
            const fitosHours = rounded(hourTypes["4"] || 0);
            const nocturnHours = rounded(hourTypes["5"] || 0);
            const kms = rounded(hourTypes["6"] || 0);
            const compensationHours = rounded(hourTypes["7"] || 0);
            const absenseHours = rounded(hourTypes["3"] || 0);

            const parcelMeta = parcels.find((p) => p.id === parseInt(parcelId));
            const societyName = parcelMeta?.society?.name || "";

            let parcelName = "",
              farmName = "",
              parcel;

            if (item.workingParts) {
              for (const part of item.workingParts) {
                if (part.parcelsSnap) {
                  parcel = part.parcelsSnap.find(
                    (p) => p.id === parseInt(parcelId),
                  );
                  if (parcel) {
                    parcelName = parcel.name;
                    farmName = parcel.farm?.name || "";
                    break;
                  }
                }
              }
            }

            // Group by farm id/name only (do not include society in the key)
            const farmId = parcel?.farm?.id || parcelMeta?.farm?.id || null;
            const farmKey = farmId
              ? `farm:${farmId}`
              : `farmName:${farmName || parcelName || parcelId}`;

            if (!farmMap[farmKey]) {
              farmMap[farmKey] = {
                societyNames: new Set(),
                farmName: farmName || parcelName || "",
                parcelNames: new Set(),
                normalHours: 0,
                extraHours: 0,
                fitosHours: 0,
                nocturnHours: 0,
                kms: 0,
                compensationHours: 0,
                absenseHours: 0,
              };
            }

            const entry = farmMap[farmKey];
            if (societyName) entry.societyNames.add(societyName);
            if (parcelName) entry.parcelNames.add(parcelName);
            entry.normalHours += normalHours;
            entry.extraHours += extraHours;
            entry.fitosHours += fitosHours;
            entry.nocturnHours += nocturnHours;
            entry.kms += kms;
            entry.compensationHours += compensationHours;
            entry.absenseHours += absenseHours;
          },
        );

        return Object.values(farmMap).map((f) => {
          const totalHours =
            Math.trunc((f.normalHours || 0) + (f.extraHours || 0)) || 0;
          return {
            A: periodo,
            B: employeeCode,
            C: employeeName,
            D: f.farmName,
            E: contractType,
            F: totalHours,
            G: status,
            H: Math.round((f.normalHours || 0) * 100) / 100,
            I: Math.round((f.extraHours || 0) * 100) / 100,
            J: Math.round((f.compensationHours || 0) * 100) / 100,
            K: Math.round((f.absenseHours || 0) * 100) / 100,
            L: Math.round((f.nocturnHours || 0) * 100) / 100,
            M: Math.round((f.fitosHours || 0) * 100) / 100,
            N: Math.round((f.kms || 0) * 100) / 100,
            O: peonadasTotal,
          };
        });
      });

      data.unshift({
        A: "Periodo",
        B: "Cod. Empl.",
        C: "Nombre empleado",
        D: "Finca",
        E: "Tipo de contrato",
        F: "Nº total horas",
        G: "Enviados: Jornadas / Conceptos",
        H: "H. normales",
        I: "H. Extras",
        J: "Compensación",
        K: "H. Ausencia",
        L: "H. nocturnas",
        M: "Fitos",
        N: "KMS",
        O: "JJRR/Partes",
      });

      const filename = `Partes_${moment(new Date()).format("DDMMYYYYHHmm")}`;
      const sheetName = "Listado de Partes";

      const ws = XLSX.utils.json_to_sheet(data, {
        header: [
          "A",
          "B",
          "C",
          "D",
          "E",
          "F",
          "G",
          "H",
          "I",
          "J",
          "K",
          "L",
          "M",
          "N",
          "O",
          "P",
        ],
        skipHeader: true,
        UTC: true,
        dateNF: "dd-mm-yyyy hh:mm;@",
      });

      const wb = XLSX.utils.book_new();

      wb.Props = {
        Title: "Listado de Partes",
        Subject: "Exportación de Partes",
        Author: "Agromove",
        CreatedDate: new Date(),
      };

      wb.SheetNames.push(sheetName);
      wb.Sheets[sheetName] = ws;

      XLSX.writeFile(wb, `${filename}.xlsx`, {
        bookType: "xlsx",
        type: "binary",
      });
    });
  };

  return (
    <>
      <div className="row justify-content-center mt-5">
        <div className="col-12 col-md-11 marg-btm-1rem marg-top-40px">
          <h3 className="title">{t("forms.workingPartSnap.title")}</h3>
        </div>
      </div>
      <Breadcrumb items={breads} />
      <div className="row justify-content-center">
        <div className="col-12 col-md-11">
          <Filter
            onSubmit={handlerOnSubmitFilter}
            onClean={handlerCleanFilter}
            onFilterEmployee={handlerFilterEmployee}
            initialValues={filters}
          />
        </div>
      </div>
      <div className="row justify-content-center">
        <div className="col-12 col-md-11">
          <QueryClientProvider client={queryClient}>
            <List
              className="marg-top-1rem"
              filters={filters}
              onExport={exportToExcel}
            />
          </QueryClientProvider>
        </div>
      </div>
    </>
  );
};

WorkingPartFilterListContainer.propTypes = {};

export default WorkingPartFilterListContainer;
